import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Modal } from "react-bootstrap";
import styled from "styled-components";
import { IAssetResponse } from "../../../interfaces";
import { useScreenshotUrl } from "./use-screenshot-url";

// Scale is relative to "fits the pane's width", not to the image's native
// pixels: 1 means the screenshot spans the pane exactly, and the (usually very
// tall) rest of it is reached by scrolling.
const FIT_WIDTH = 1;
const MIN_SCALE = 0.25;
const MAX_SCALE = 5;
const ZOOM_STEP = 1.25;

const clamp = (value: number) => Math.min(Math.max(value, MIN_SCALE), MAX_SCALE);

const ModalContent = styled.div`
  display: flex;
  height: 80vh;
`;

/* Holds the controls, which must stay put while the screenshot scrolls under
   them — an absolutely positioned child of a scrolling box scrolls with it. */
const ImagePane = styled.div`
  flex: 3;
  position: relative;
  overflow: hidden;
  background-color: #000;
`;

const ScrollArea = styled.div<{ dragging: boolean }>`
  position: absolute;
  inset: 0;
  overflow: auto;
  cursor: ${(props) => (props.dragging ? "grabbing" : "grab")};
`;

const ScreenshotImage = styled.img<{ scalePercent: number }>`
  display: block;
  /* Percentage of the pane's width, so 100% always fits exactly. Auto margins
     centre it when zoomed out and collapse to 0 when it overflows. */
  width: ${(props) => props.scalePercent}%;
  height: auto;
  margin: 0 auto;
  user-select: none;
  -webkit-user-drag: none;
`;

const ViewportMessage = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #adb5bd;
  font-size: 0.9rem;
`;

const MetadataContainer = styled.div`
  flex: 1;
  padding: 20px;
  background-color: #f8f9fa;
  overflow-y: auto;
`;

const MetadataTitle = styled.h6`
  margin-bottom: 16px;
  color: #495057;
`;

const MetadataJson = styled.pre`
  background-color: #fff;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 12px;
  font-size: 12px;
  margin: 0;
  white-space: pre-wrap;
  word-wrap: break-word;
`;

const ZoomControls = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 10;
`;

const ZoomLevel = styled.span`
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  border-radius: 4px;
  padding: 8px 10px;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
`;

const ZoomButton = styled.button`
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    background-color: rgba(0, 0, 0, 0.9);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ViewportHint = styled.div`
  position: absolute;
  bottom: 10px;
  right: 10px;
  background-color: rgba(0, 0, 0, 0.6);
  color: #dee2e6;
  border-radius: 4px;
  padding: 6px 10px;
  font-size: 12px;
  z-index: 10;
  pointer-events: none;
`;

interface Props {
  taskId: string;
  activityId: string;
  show: boolean;
  screenshot: IAssetResponse | null;
  onClose: () => void;
}

export const ScreenshotModal: React.FC<Props> = ({
  taskId,
  activityId,
  screenshot,
  show,
  onClose,
}) => {
  const { url, failed } = useScreenshotUrl(
    taskId,
    activityId,
    screenshot?._id,
    show
  );
  const [scale, setScale] = useState(FIT_WIDTH);
  const [dragging, setDragging] = useState(false);

  const viewportRef = useRef<HTMLDivElement | null>(null);
  // The point to hold still across a zoom, captured before the scale changes
  // and reapplied once the browser has re-laid the image out.
  const anchorRef = useRef<{
    fx: number;
    fy: number;
    px: number;
    py: number;
  } | null>(null);
  const dragRef = useRef<{
    clientX: number;
    clientY: number;
    scrollLeft: number;
    scrollTop: number;
  } | null>(null);

  /**
   * Zoom around a fixed point. `anchor` is in client coordinates (the cursor);
   * without one the centre of the pane is held instead, which is what the
   * buttons want.
   */
  const zoomTo = useCallback(
    (next: number, anchor?: { x: number; y: number }) => {
      const viewport = viewportRef.current;
      const clamped = clamp(next);

      if (viewport && viewport.scrollWidth && viewport.scrollHeight) {
        const rect = viewport.getBoundingClientRect();
        const px = anchor ? anchor.x - rect.left : viewport.clientWidth / 2;
        const py = anchor ? anchor.y - rect.top : viewport.clientHeight / 2;

        anchorRef.current = {
          fx: (viewport.scrollLeft + px) / viewport.scrollWidth,
          fy: (viewport.scrollTop + py) / viewport.scrollHeight,
          px,
          py,
        };
      }

      setScale(clamped);
    },
    []
  );

  const zoomIn = () => zoomTo(scale * ZOOM_STEP);
  const zoomOut = () => zoomTo(scale / ZOOM_STEP);
  const fitWidth = () => {
    anchorRef.current = null;
    setScale(FIT_WIDTH);
    viewportRef.current?.scrollTo({ top: 0, left: 0 });
  };

  // Restore the anchored point now that the new width has been applied. This
  // has to run before paint, otherwise the image visibly jumps.
  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const anchor = anchorRef.current;
    anchorRef.current = null;

    if (!viewport || !anchor) {
      return;
    }

    viewport.scrollLeft = anchor.fx * viewport.scrollWidth - anchor.px;
    viewport.scrollTop = anchor.fy * viewport.scrollHeight - anchor.py;
  }, [scale]);

  // Start each screenshot fitted to the width and scrolled to the top.
  useEffect(() => {
    anchorRef.current = null;
    setScale(FIT_WIDTH);
    viewportRef.current?.scrollTo({ top: 0, left: 0 });
  }, [show, screenshot]);

  // A plain wheel scrolls the screenshot — that is the main way to read a long
  // capture — and ctrl/⌘ + wheel zooms, as everywhere else. React attaches
  // wheel handlers passively, so preventDefault only works from a native
  // listener registered with { passive: false }.
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) {
      return;
    }

    const onWheel = (event: WheelEvent) => {
      if (!event.ctrlKey && !event.metaKey) {
        return;
      }
      event.preventDefault();
      const factor = Math.pow(ZOOM_STEP, -event.deltaY / 100);
      zoomTo(scale * factor, { x: event.clientX, y: event.clientY });
    };

    viewport.addEventListener("wheel", onWheel, { passive: false });
    return () => viewport.removeEventListener("wheel", onWheel);
  }, [scale, zoomTo, url]);

  // Drag to pan. Listening on the window keeps the drag alive when the cursor
  // leaves the pane, which the old mouseleave-cancels-drag version did not.
  useEffect(() => {
    if (!dragging) {
      return;
    }

    const onMouseMove = (event: MouseEvent) => {
      const viewport = viewportRef.current;
      const start = dragRef.current;
      if (!viewport || !start) {
        return;
      }
      viewport.scrollLeft = start.scrollLeft - (event.clientX - start.clientX);
      viewport.scrollTop = start.scrollTop - (event.clientY - start.clientY);
    };

    const onMouseUp = () => {
      dragRef.current = null;
      setDragging(false);
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [dragging]);

  const handleMouseDown = (event: React.MouseEvent) => {
    const viewport = viewportRef.current;
    if (!viewport) {
      return;
    }
    dragRef.current = {
      clientX: event.clientX,
      clientY: event.clientY,
      scrollLeft: viewport.scrollLeft,
      scrollTop: viewport.scrollTop,
    };
    setDragging(true);
    // Suppresses the browser's own image drag.
    event.preventDefault();
  };

  const handleDoubleClick = (event: React.MouseEvent) => {
    const target = scale > FIT_WIDTH ? FIT_WIDTH : 2;
    zoomTo(target, { x: event.clientX, y: event.clientY });
  };

  return (
    <Modal show={show} onHide={onClose} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>Screenshot Details</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: 0 }}>
        {screenshot && (
          <ModalContent>
            <ImagePane>
              <ZoomControls>
                <ZoomButton onClick={zoomOut} disabled={scale <= MIN_SCALE}>
                  −
                </ZoomButton>
                <ZoomLevel>{Math.round(scale * 100)}%</ZoomLevel>
                <ZoomButton onClick={zoomIn} disabled={scale >= MAX_SCALE}>
                  +
                </ZoomButton>
                <ZoomButton onClick={fitWidth} disabled={scale === FIT_WIDTH}>
                  Fit width
                </ZoomButton>
              </ZoomControls>

              <ScrollArea
                ref={viewportRef}
                dragging={dragging}
                onMouseDown={handleMouseDown}
                onDoubleClick={handleDoubleClick}
              >
                {url && (
                  <ScreenshotImage
                    src={url}
                    alt="Full screenshot"
                    scalePercent={scale * 100}
                    draggable={false}
                  />
                )}
              </ScrollArea>

              {!url && (
                <ViewportMessage>
                  {failed
                    ? "This screenshot could not be loaded."
                    : "Loading screenshot..."}
                </ViewportMessage>
              )}

              {url && (
                <ViewportHint>
                  Drag or scroll to pan · ⌘/ctrl + scroll to zoom
                </ViewportHint>
              )}
            </ImagePane>

            <MetadataContainer>
              <MetadataTitle>Screenshot Metadata</MetadataTitle>
              <MetadataJson>
                {JSON.stringify(screenshot.metadata || {}, null, 2)}
              </MetadataJson>
            </MetadataContainer>
          </ModalContent>
        )}
      </Modal.Body>
    </Modal>
  );
};
